// قفل صفحه پس از بی‌فعالیتی (BR-IAM-050، AC-IAM-050.2): پیش‌فرض ۳۰ دقیقه؛ تنظیم iam.session_lock_minutes (۱..۳۰)
// ادامه فقط با ورود مجدد رمز (Popup Keycloak با prompt=login). در حالت قفل توکن مستأجر از حافظه حذف و تمدیدها متوقف می‌شوند.
import LockIcon from '@mui/icons-material/Lock';
import Backdrop from '@mui/material/Backdrop';
import Button from '@mui/material/Button';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { useTranslation } from '@phoenix/i18n';
import { useCallback, useEffect, useId, useRef, useState, type ReactNode } from 'react';
import { useAccess } from './AccessProvider';
import { useAuth } from './AuthProvider';
import { useTenantSession } from './TenantSessionProvider';

export const SESSION_LOCK_SETTING = 'iam.session_lock_minutes';
export const DEFAULT_SESSION_LOCK_MINUTES = 30;

const ACTIVITY_EVENTS = ['pointerdown', 'pointermove', 'keydown', 'wheel', 'touchstart', 'scroll'] as const;

/**
 * مقدار مؤثر iam.session_lock_minutes از me/access (AP-IAM-01، C-AGENT-185) برای همه اعضا بدون cfg.settings.view؛ پیش از پاسخ
 * یا در خطا پیش‌فرض ۳۰؛ محدود به ۱..۳۰ (جایگزین خواندن تنظیم CFG در C-AGENT-134 — C-AGENT-224).
 */
export function useSessionLockMinutes(): number {
  const access = useAccess();
  const value = Number(access.sessionLockMinutes);
  if (!Number.isFinite(value) || value <= 0) {
    return DEFAULT_SESSION_LOCK_MINUTES;
  }
  return Math.min(DEFAULT_SESSION_LOCK_MINUTES, Math.max(1, Math.trunc(value)));
}

/** تایمر بی‌فعالیتی: پس از minutes دقیقه بدون رویداد کاربر، onIdle. */
export function useIdleTimer(minutes: number, onIdle: () => void, enabled: boolean): void {
  const callback = useRef(onIdle);
  callback.current = onIdle;

  useEffect(() => {
    if (!enabled) {
      return undefined;
    }
    let timer: ReturnType<typeof setTimeout>;
    const arm = () => {
      clearTimeout(timer);
      timer = setTimeout(() => callback.current(), minutes * 60_000);
    };
    arm();
    ACTIVITY_EVENTS.forEach((name) => window.addEventListener(name, arm, { passive: true }));
    return () => {
      clearTimeout(timer);
      ACTIVITY_EVENTS.forEach((name) => window.removeEventListener(name, arm));
    };
  }, [minutes, enabled]);
}

export interface ScreenLockProps {
  children: ReactNode;
  /** مقدار ثابت برای تست؛ در غیر این صورت از تنظیم. */
  minutes?: number;
}

export function ScreenLock({ children, minutes }: ScreenLockProps) {
  const { t } = useTranslation('auth');
  const auth = useAuth();
  const tenant = useTenantSession();
  const configured = useSessionLockMinutes();
  const effective = minutes ?? configured;
  const [locked, setLocked] = useState(false);
  const [busy, setBusy] = useState(false);
  const [failed, setFailed] = useState(false);
  const titleId = useId();
  const contentRef = useRef<HTMLDivElement>(null);

  // محتوای پشت قفل غیرتعاملی (inert): نه فوکوس، نه کلیک.
  useEffect(() => {
    if (contentRef.current) {
      contentRef.current.inert = locked;
    }
  }, [locked]);

  const lock = useCallback(() => {
    setLocked(true);
    tenant.suspend();
    auth.pauseRenew();
  }, [tenant, auth]);

  useIdleTimer(effective, lock, !locked && tenant.status === 'active');

  const unlock = async () => {
    setBusy(true);
    setFailed(false);
    try {
      const ok = await auth.reauthenticate();
      if (ok) {
        auth.resumeRenew();
        if (await tenant.resume()) {
          setLocked(false);
          return;
        }
      }
      setFailed(true);
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <div ref={contentRef} aria-hidden={locked} style={locked ? { filter: 'blur(6px)' } : undefined}>
        {children}
      </div>
      {locked && (
        <Backdrop open sx={{ zIndex: (theme) => theme.zIndex.modal + 10, bgcolor: 'rgba(15,20,23,0.6)' }}>
          <Paper
            role="dialog"
            aria-modal="true"
            aria-labelledby={titleId}
            sx={{ p: 4, maxWidth: 420, textAlign: 'center' }}
            data-screen-id="STAFF-SCREEN-LOCK"
          >
            <Stack spacing={2} alignItems="center">
              <LockIcon color="primary" sx={{ fontSize: 48 }} />
              <Typography id={titleId} variant="h2">
                {t('auth:lock.title')}
              </Typography>
              <Typography color="text.secondary">{t('auth:lock.description')}</Typography>
              {failed && <Typography color="error">{t('auth:lock.failed')}</Typography>}
              <Button
                variant="contained"
                onClick={() => void unlock()}
                disabled={busy}
                autoFocus
                data-testid="unlock"
              >
                {t('auth:lock.unlock')}
              </Button>
              <Button onClick={() => void auth.logout()} disabled={busy}>
                {t('auth:account.logout')}
              </Button>
            </Stack>
          </Paper>
        </Backdrop>
      )}
    </>
  );
}
